import * as THREE from 'three';
import { initConsole } from './ui/console';
import { lockedTarget } from './sim/lockon';
import { lockRing } from './world/pools';
import { updateSchedule } from './sim/fx';
import { initResolution, updateResolution } from './platform/resolution';
import { setListener } from './platform/audio-engine';
import { setIntensity, setScene } from './platform/music';
import { SFX } from './platform/audio';
import { chooseReward } from './ui/progress';
import { openLevelUp, chooseUpgrade, openChestReveal } from './ui/levelup';
import { closeHangar, hangarOpen, initHangarControls, openHangar, setPose, updateHangarCamera } from './ui/hangar';
import { closeCodex, codexOpen, initCodexControls, openCodex, updateCodex } from './ui/codex';
import { codexFrame } from './render/codex-model';
import { FIT as CODEX_FIT } from './render/codex-fit';
import { bullets, debris, ebullets, missiles, orbs } from './world/pools';
import { perf } from './platform/perf';
import { updatePerfOverlay, initPerfOverlay } from './ui/perf-overlay';
import { damp, lerp } from './core/math';
import { RNG } from './core/rng';
import { loadoutDigest, take, weapons as weaponsRef, passives as passivesRef } from './sim/loadout';
import { boss, enemies } from './entities/enemies';
import { enemyField, syncEnemyField } from './render/enemy-field';
import { syncBossField } from './render/boss-field';
import { syncWarnFx } from './render/warn-fx';
import { playerRing } from './render/gadgets';
import { syncFxInstances } from './render/fx-instances';
import { elStage, elStart, initDom, initDomHeadless } from './platform/dom';
import { initRenderer, renderer } from './platform/renderer';
import { BLOOM, initPostFX, postActive, renderPostFX, setPostFX, tunePostFX } from './render/postfx';
import { contactShadowMesh } from './render/contact-shadow';
import { initEnv } from './render/env';
import { loadQuality, QUALITY, setQuality } from './platform/quality';
import { loadMeta } from './platform/storage';
import { CAM_OFF, camera, hemi, resize, rim, rim2, scene, sun } from './render/scene';
import { hangarGroup } from './render/hangar-scene';
import { updateEnemies } from './sim/enemies';
import { STATE, initDmgPool, updateDmg } from './sim/fx';
import { initInput, input } from './sim/input';
import { updatePlayer } from './sim/player';
import { takeoffCost } from './sim/flight';
import { updateCamera } from './sim/camera';
import { applyCamIntro, camIntroActive, preCamIntro } from './sim/cam-intro';
import { updateFx, updatePickups, updateProjectiles } from './sim/projectiles';
import { updateStageFlow } from './sim/stage-flow';
import { startRun, updateHud } from './ui/hud';
import { initStartScreen } from './ui/screens/start';
import { loadUIPrefs } from './platform/ui-prefs';
import {
  ARENA, STAGES, VAULT, animField, applyTheme, arenaArea, ashMesh, buildTerrain, cloudMesh,
  farSkyline, farSkyline2, floorMesh, dustMesh, macroMesh, obstacles, ramps, skyMesh, terrainGroup
} from './world/arena';
import { sunFollow } from './render/scene';
import { flyActive, flyTo, toggleFly, updateFly } from './render/flycam';
import { registerFxPools } from './world/pools';
import { P, equipMech } from './world/player';
import { ensureParts } from './sim/meta-parts';
import { metStep } from './sim/metrics';
import { updateMechAnim } from './entities/mech-anim';
import { initVfxPool, vfxAlive, vfxEnable, vfxProbe, vfxReady } from './render/vfx-pool';
import { initTrails, updateTrails } from './render/trails';
import { updateWeaponFx } from './render/weapon-fx';
/* ============================================================
   主迴圈
   ============================================================ */
/* 固定時間步。模擬永遠以 1/60 秒推進，與螢幕更新率無關。

   改這個的理由：原本 dt 由 performance.now() 的差值推導，代表同一顆種子在 144Hz
   與 60Hz 會跑出不同結果 —— 衝刺位移、damp 收斂、之後要加的熱量累積都會隨螢幕
   更新率改變，調出來的手感只在開發者自己的機器上成立。而且沒有決定性就無法用
   重播驗證遷移有沒有改壞行為。

   渲染用 alpha 對位置內插，避免 60Hz 模擬在高更新率螢幕上看起來卡。 */
export var FIXED_STEP = 1 / 60;
export var MAX_CATCHUP = 15;              // 分頁切回來時最多補 15 步，剩下的丟棄
export var accum = 0;
export var simSteps = 0;                  // 累計模擬步數，決定性測試用
/* 這兩個由關卡流程與開局流程重置，同樣要走 setter。 */
export function setAccum(v) { accum = v; }
export function setSimSteps(v) { simSteps = v; }
export var lastT = performance.now();

export function simStep(dt) {
  simSteps++;
  P.prevX = P.pos.x; P.prevZ = P.pos.z; P.prevY = P.y;
  for (var i = 0; i < enemies.length; i++) { var en = enemies[i]; en.prevX = en.pos.x; en.prevZ = en.pos.z; en.prevY = en.y; }

  STATE.timeScale = damp(STATE.timeScale, 1, 9, dt);
  var d = dt * STATE.timeScale;
  STATE.runT += d;
  updateStageFlow(d);
  updatePlayer(d, input);
  updateEnemies(d);
  updateProjectiles(d);
  updatePickups(d);
  updateSchedule(d);
  updateFx(d);
  updateHud(d);
  metStep(d, enemies.length);   // 預設關閉，只有診斷腳本會打開（sim/metrics.ts）
  /* 升級介面在**模擬步內**開，不是每幀開。

     原本放在 tick() 裡（每幀一次），於是「哪一步暫停」取決於畫面更新率：
     30Hz 一幀跑兩個模擬步，玩家在第一步升級、但要等第二步也跑完才會暫停 ——
     升級效果晚一步生效，整場從此岔開。決定性測試就是這樣紅的。
     這個依賴一直存在，是 M9 把密度拉高、升級變密之後才浮出來。 */
  /* `bossUp`（Boss 擊破獎勵）走同一條路徑、同一個介面 —— 分開開的話
     兩個暫停旗標一定會有某個路徑漏掉其中一個（`ui/levelup.ts` 檔頭記過）。 */
  /* 開箱揭示排在升級之前：撿到箱子那一瞬間常常同時升級（箱子多半是
     從一堆屍體裡撿的），先給玩家看他撿到什麼才對得上因果。
     跟升級介面共用 `STATE.reward` 這個暫停旗標 —— 兩個暫停旗標一定會有
     某個路徑漏掉其中一個（`ui/levelup.ts` 檔頭記過）。 */
  if (P.chestPicks > 0 && !STATE.reward) openChestReveal();
  if ((P.pendingLv > 0 || P.bossUp > 0) && !STATE.reward) openLevelUp();
}

export function interpolate(a) {
  P.viewX = lerp(P.prevX, P.pos.x, a);
  P.viewZ = lerp(P.prevZ, P.pos.z, a);
  P.viewY = lerp(P.prevY, P.y, a);
  if (P.rig) P.rig.root.position.set(P.viewX, P.viewY, P.viewZ);
  /* 接地綠環跟著位置但不跟著姿態（規格 §3）——
     機體被大型 Boss 擋住、或陣亡倒地時，這一圈還在地上。
     **飛起來時它留在地面**，那正是「你的降落點在哪」的指示器。 */
  var pr = playerRing();
  pr.position.set(P.viewX, 0.03, P.viewZ);
  pr.visible = STATE.running && P.alive;
  for (var i = 0; i < enemies.length; i++) {
    var en = enemies[i];
    /* 高度也要內插。**寫死 0 的話走上高台的敵人會整隻沉在平台裡** ——
       貼地高度是模擬層每步算的（`e.y`），渲染層只負責把兩步之間補平。 */
    en.root.position.set(lerp(en.prevX, en.pos.x, a), lerp(en.prevY || 0, en.y, a), lerp(en.prevZ, en.pos.z, a));
  }
  /* 小怪是實例化畫的（每個 part 一顆 InstancedMesh），而且它們沒有
     Object3D 造型 —— 矩陣由 part 表直接算。一定要在 interpolate 之後：
     這一幀要畫的是內插後的位置，不是模擬步的位置。 */
  syncEnemyField(enemies, STATE.runT);
  /* Boss 走真 rig，動畫是每隻自己的 `upd()`，跟小怪完全不同一條路
     （`render/boss-field.ts`）。死亡的四拍編排也在那裡推進。 */
  syncBossField(enemies, STATE.runT);
  syncWarnFx();
  syncFxInstances();
}

/* tick 是純推進：吃一個時間戳，跑該跑的模擬步與渲染。不碰 requestAnimationFrame，
   所以 Node 裡可以直接呼叫。frame 只是「掛回 rAF 再呼叫 tick」的瀏覽器包裝。 */
export function frame(now) {
  requestAnimationFrame(frame);
  tick(now);
}

/* 機體動畫的唯一入口。跑在**畫面幀**上而不是模擬步上 ——
   八個彈簧的平滑跑在固定 60Hz 上會被顯示率切成階梯（見 `entities/mech-anim.ts`）。

   `showYaw` 有值＝展示模式（開始畫面與機庫）：機體釘在原點、
   不吃戰鬥狀態。打完一場回機庫時陣亡姿勢會自己在 0.3 秒內化掉，
   不用另外「清乾淨」—— 那正是舊版 restPose 存在的理由。 */
function animateMech(dt, showYaw?) {
  var r = P.rig; if (!r) return;
  if (showYaw !== undefined) {
    r.root.position.set(0, 0, 0); r.root.rotation.y = 0;
    /* `idle: true` = 加上怠速表演（呼吸、重心轉移、頭部掃視、每 4.6 秒一次的
       排氣或伺服校正）。機庫是唯一近距離看機體的畫面，站定不動讀起來像模型。 */
    updateMechAnim(r, dt, { speed: 0, speedMax: 1, aimYaw: showYaw, moveYaw: showYaw, dash: false, ult: 0, dead: false, hidden: false, idle: true });
    return;
  }
  var sp = Math.hypot(P.vel.x, P.vel.z);
  updateMechAnim(r, dt, {
    speed: sp, speedMax: Math.max(0.01, P.s.speed),
    aimYaw: P.faceAim, moveYaw: P.faceMove,
    dash: P.dashT > 0,
    /* 推進器：`fly` 是「正在噴」（噴焰全開），`air` 是「離地了」（腿收起的滑行姿勢）。
       兩個分開餵 —— 燃料耗盡後還在半空中的那一段是**沒有噴焰的下墜**，
       那一拍看得出「你掉下來了」，是這個系統最重要的一個回饋。 */
    fly: P.flying ? 1 : 0, air: P.y > 0.35,
    /* 大招是**持續型**（光束 2.3 秒、旋刃 4 秒），不是原型的單發。
       所以蓄力值餵「還在放大招嗎」，機體整段維持發射姿態。 */
    ult: P.ultT >= 0 ? 1 : 0,
    dead: !P.alive,
    /* 無敵閃爍：跟舊版同一條式子，只是改由動畫層統一收。
       `!camIntroActive()`：閃爍是靠 `iframe` **每步遞減**交替出來的，而出擊過場
       期間模擬是停的 —— 那條式子會凍成一個固定值，機體整段過場直接隱形
       （實測就是這樣：中繼機位那張只有空地）。過場中玩家還沒開始玩，
       本來也不需要無敵回饋。 */
    hidden: !camIntroActive() && P.iframe > 0 && Math.floor(P.iframe * 24) % 2 === 0
  });
}

export var mapTopH = 0;
var mapFog: any = null;
export function tick(now) {
  perf.end('frame'); perf.begin('frame');   // 量的是「上一幀開始到這一幀開始」的間隔
  // 下限 0 是必要的：lastT 初始化成真實時鐘，外部驅動（測試、重播）第一幀的差值會是負的
  var real = Math.max(0, Math.min(0.25, (now - lastT) / 1000)); lastT = now;

  /* 音樂的場景切換（`platform/music.ts`）。**放在所有分支之前**，
     因為標題／機庫／圖鑑走的是下面的 else 分支，而它們都該播主題曲。
     `setScene` 對同一個音軌是 no-op，每幀呼叫沒有成本。
     判準用 `STATE.running`：暫停時它仍是 true，所以暫停不會切歌（正確 ——
     暫停只是停下來看面板，不是離開戰場）。 */
  setScene(STATE.running && !STATE.over ? 'run' : 'menu');

  /* `!camIntroActive()`：出擊過場（`sim/cam-intro.ts`）期間**模擬整個停住**
     —— 使用者選的是「過場結束才開始」。停的是 `simStep`，不是畫面：
     下面 else 分支照樣跑鏡頭與機體動畫，過場是活的，只是計時還沒開始，
     玩家也不會在還沒看清自己的時候被打。 */
  if (STATE.running && !STATE.reward && !STATE.paused && !camIntroActive()) {
    accum += real;
    var steps = 0;
    perf.begin('sim');
    /* `!STATE.reward`：升級畫面一開就立刻停止這一幀剩下的補步，
       否則暫停會晚一步發生 —— 又變回原本那個 frame-rate 依賴。 */
    while (accum >= FIXED_STEP && steps < MAX_CATCHUP && !STATE.reward) {
      simStep(FIXED_STEP); accum -= FIXED_STEP; steps++;
    }
    perf.end('sim'); perf.value('steps', steps);
    if (accum >= FIXED_STEP) accum = 0;          // 補不完就丟棄，不讓積欠無限累積
    interpolate(accum / FIXED_STEP);
    animateMech(real * STATE.timeScale);
    animField(real, P.viewX, P.viewZ);   // 光影斑／火／探照燈／跟著玩家的餘燼
    /* 陰影相機跟著**看的人**：一般是玩家，自由視角時是飛行鏡頭 ——
       不跟的話飛出去五十公尺，看到的地形全部沒有影子。 */
    if (flyActive()) sunFollow(camera.position.x, camera.position.z);
    else { sunFollow(P.viewX, P.viewZ); updateCamera(real * STATE.timeScale); }
    /* 聽者跟著鏡頭的中心走（＝玩家）。距離衰減與左右聲相都靠它 ——
       少了這一行，畫面外的爆炸會跟腳邊的一樣大聲（第一版就是這樣）。 */
    setListener(P.viewX, P.viewZ);
    /* 音樂強度（`platform/music.ts`）。三個來源疊起來：

       ① **時間**：20 分鐘從 0.12 推到 0.72，這是整場的斜坡。
       ② **周圍密度**：22 單位內的敵人數（`near20`），上限 +0.28 —— 這是
          「現在有多危險」，也是唯一會在同一分鐘內上下的來源。
       ③ **Boss**：直接墊高到至少 0.85。Boss 出場該有音樂上的事件感。

       用實際狀態而不是「第幾波」：玩家跑掉、清空、被圍住，音樂都該跟著動。 */
    var mi = 0.12 + Math.min(1, STATE.runT / 1200) * 0.60
      + Math.min(1, STATE.near20 / 45) * 0.28;
    if (STATE.phase === 'boss') mi = Math.max(mi, 0.85);
    setIntensity(mi);
  } else {
    accum = 0;
    updateFx(real);
    /* **圖鑑要排在 `STATE.running` 之前判斷。** 對局中按 C 開圖鑑會順便暫停，
       而暫停時 `STATE.running` 仍然是 true —— 排在後面的話戰場鏡頭會把
       圖鑑的鏡頭每幀蓋掉，症狀是「圖鑑開了但畫面還在戰場上」。 */
    if (codexOpen()) {
      /* 圖鑑的鏡頭整組在 `ui/codex.ts`（跟機庫同一套：軌道中心在展品上、
         版面偏移靠視錐而不是移注視點）。**機體動畫不跑** ——
         圖鑑裡那台是半透明的比例尺，它動起來會搶走展品的注意力。 */
      updateCodex(real, now * 0.001);
    }
    else if (STATE.running || STATE.over) {
      P.viewX = P.pos.x; P.viewZ = P.pos.z; P.viewY = P.y;
      /* 機體的世界位置平常是 `interpolate()` 設的，而那個函式在模擬停下時
         不跑 —— 過場期間不補這一行，機體會留在上一個畫面（標題展示）
         把它釘住的原點上。 */
      if (P.rig) P.rig.root.position.set(P.viewX, P.viewY, P.viewZ);
      animateMech(real);
      if (!flyActive()) {
        /* 出擊過場也走這條（模擬停著、`STATE.running` 是 true）。
           `preCamIntro` 要排在前面：它把上一幀的戰場機位還給 `updateCamera`，
           否則它的 damp 會從過場的混合值續算，終點就跟著跑掉了。 */
        preCamIntro(); updateCamera(real); applyCamIntro(real);
      }
      // 陰影相機：過場期間鏡頭離玩家很遠，不跟的話機體腳下沒有影子
      sunFollow(P.viewX, P.viewZ);
    }
    else {
      var a = now * 0.00012;
      if (hangarOpen()) {
        /* 機庫的鏡頭整組在 `ui/hangar.ts`：軌道中心在機體中軸上，
           「機體要落在兩欄之間」靠視錐偏移達成而不是移動注視點
           （移注視點會讓機體繞著偏移的圓心跑，讀起來像在漂移）。 */
        updateHangarCamera(real);
        animateMech(real, 0);
      } else {
        /* 標題頁的展示鏡頭（2026-08-05 拉近）：舊值是距離 26、高 20 的俯視，
           機體只有畫面高度的 8% —— 而重做後的標題頁**右半邊整片留給它**，
           那個尺寸下看起來像一個誤入的小物件。改成距離 15、高 8.5 的水平視角。
           這不是戰場鏡頭（`CAM.preset` 是使用者的決定，這裡碰不到它）。 */
        camera.position.set(Math.sin(a) * 15, 8.5, Math.cos(a) * 15);
        camera.lookAt(0, 2.6, 0);
        animateMech(real, a * 3);
      }
    }
  }
  updateDmg(real);
  /* 武器特效與拖尾。吃 `timeScale` —— hitstop 的那 0.06 秒裡特效也要跟著慢，
     不然畫面凍住而火花照常飛，那一下會讀成掉幀而不是頓挫。
     排在鏡頭更新之後：緞帶的寬度方向是「行進方向 × 到鏡頭的方向」算出來的，
     用上一幀的鏡頭位置會在轉向時抖一格。 */
  updateWeaponFx(real * STATE.timeScale);
  updateTrails(real * STATE.timeScale);
  /* 自由視角（主控台 `fly`）。**蓋在所有鏡頭邏輯之後、俯視覆寫之前** ——
     戰場鏡頭每幀都會把 `camera.position` 寫回玩家身上，排在它前面的話
     兩者會互相打架（畫面抖動）。上面幾個分支已經用 `flyActive()` 擋掉了
     `updateCamera`，這裡只負責移動鏡頭本身。 */
  updateFly(real);
  perf.begin('render');
  /* 俯視覆寫（只給 `tools/map-shot.js`）。蓋在所有鏡頭邏輯之後 ——
     蓋在前面的話 `updateCamera` 會把它每幀改回去。 */
  if (mapTopH > 0) {
    camera.position.set(0, mapTopH, 0.01); camera.lookAt(0, 0, 0);
    camera.updateMatrixWorld();
    /* 俯視時把霧收掉：霧是照**與鏡頭的距離**算的，鏡頭拉到 150 單位高
       的話整張地圖都在霧的遠端，拍出來是一片背景色（第一版就是全黑）。 */
    if (!mapFog) { mapFog = scene.fog; scene.fog = null; }
    /* 霧一關，**場外的遠景廢墟就變成一圈亮灰色板子**（它們本來全靠霧融掉）。
       俯視是拿來看版面的，那一圈只會干擾判讀 —— 直接關掉。 */
    if (farSkyline) farSkyline.visible = false;
    if (farSkyline2) farSkyline2.visible = false;
  } else if (mapFog) {
    scene.fog = mapFog; mapFog = null;
    if (farSkyline) farSkyline.visible = true;
    if (farSkyline2) farSkyline2.visible = true;
  }
  /* 後製開著就走 composer（見 `render/postfx.ts`），關掉是真的零成本。
     `info.reset()` 是因為後製把 `autoReset` 關掉了（理由見 postfx.ts）——
     少了這一行，draw call 統計會一路累加到爆表。 */
  if (renderer) {
    if (!renderer.info.autoReset) renderer.info.reset();
    if (postActive()) renderPostFX(); else renderer.render(scene, camera);
  }
  perf.end('render');

  // 外層再擋一次：countAlive 是 O(n)，面板沒開就不該算
  if (perf.detailed) perf.collect(renderer, {
    enemies: enemies.length,
    bullets: bullets.countAlive(), ebullets: ebullets.countAlive(),
    debris: debris.countAlive(), orbs: orbs.countAlive(),
  });
  /* 鎖定準星跟著內插後的位置，否則它會以 60Hz 階梯跳動、比機體本身抖 */
  var lt = lockedTarget();
  if (lt) {
    lockRing.visible = true;
    lockRing.position.set(lt.root.position.x, 0.16, lt.root.position.z);
    lockRing.scale.setScalar(lt.r * 2.3);
    lockRing.rotation.z += real * 1.6;
  } else lockRing.visible = false;

  updateResolution();
  updatePerfOverlay(real * 1000);
}

/* ---------- 啟動 ----------
   模組載入本身不再有任何副作用，一切從這兩個進入點開始。
   boot()         瀏覽器用，見 src/browser.ts
   bootHeadless() Node 用，不建立 renderer、不掛事件、不做 DOM 傷害數字 */
export function common() {
  applyTheme(STAGES[0]);
  equipMech('chassis0');
  /* 實例化渲染的註冊。放在 common 而不是 boot：headless 也要跑，
     否則測試與正式版走的是兩條不同的渲染路徑（差異只會在瀏覽器才爆）。 */
  registerFxPools();
}

export function boot() {
  initDom();
  /* **畫質要最先讀**：地面與建築的法線圖是「低畫質時根本不算」的，
     晚一步讀的話那 100 ms 的 Sobel 已經照預設的「高」跑掉了
     （見 `platform/quality.ts` 檔頭）。 */
  loadQuality();
  initResolution(initRenderer(elStage));
  /* 後製要在 renderer 建好之後、第一次 `resize()` 之前建立 ——
     `resize()` 會呼叫 `resizePostFX()`，composer 還沒建的話那次尺寸就漏掉了。 */
  initPostFX(renderer);
  /* 環境反射圖（濕地與鋼材要反射的東西）。PMREM 需要 GL context，
     所以一定要排在 renderer 之後、第一次建圖之前。 */
  initEnv(renderer);
  initDmgPool();
  /* 武器特效的池子。要在 renderer 之後（材質要 GL context 才編譯得起來），
     而且**只在瀏覽器**建 —— 貼圖是 canvas 畫出來的，headless 一律回 null，
     所有生成呼叫會自己變成空操作（見 `render/vfx-tex.ts` 檔尾）。 */
  initVfxPool(); initTrails();
  initInput();
  initPerfOverlay();
  /* 介面偏好（縮放／震動／傷害數字）要在第一次畫任何 UI 之前套用，
     否則玩家會看到介面在開機後跳一次大小。畫質與音量的 UI 改由設定頁自己初始化。 */
  loadUIPrefs();
  initConsole();
  initHangarControls();
  initCodexControls();
  window.addEventListener('resize', resize);
  resize();
  common();
  loadMeta(function () {
    /* **存檔載入後一定要重建 rig。** `common()` 早在這之前就 equipMech 過一次，
       那時 `META.equip` 還是空的 —— `equippedShape()` 讀不到裝備就回退到起手件外形，
       於是開始畫面與機庫第一次看到的機體是「全基礎型」，要手動換一次部件才會變對
       （換件走 `hangar-panel` 的 refreshRig，那條路徑一直是好的，所以症狀只在初次渲染）。 */
    ensureParts();
    equipMech(P.mech ? P.mech.id : 'chassis0');
    /* **音訊引擎在開機就建立**，不等到出擊。
       主畫面音樂要在標題畫面就播得出來，而 `audioCtx()` 是在這一步才有值 ——
       少了這一行，`music-tracks.ts` 的 deck 拿不到 context，
       症狀是「主題曲完全沒有反應，而且沒有任何錯誤」。
       這時建立的 AudioContext 會是 suspended（瀏覽器政策），
       第一次點擊由 `armGesture` 負責 resume。 */
    SFX.init();
    initStartScreen(); elStart.classList.add('show');
  });
  requestAnimationFrame(frame);
}

export function bootHeadless() {
  initDomHeadless();
  resize();
  common();
  return api;
}

/* ---------- 除錯掛勾 ----------
   只給自動化測試用（決定性驗證、之後 M2 的重播回歸）。不參與任何遊戲邏輯，
   刪掉它遊戲照常運作。暴露最小面積：驅動一幀、讀狀態、開一場指定種子的對局。 */

export var api = {
  frame: frame,
  tick: tick,
  input: input,
  perf: perf,
  // 讓 headless 測試能推進被選單擋住的流程
  chooseReward: function (i) { chooseReward(i); },
  chooseUpgrade: function (i) { chooseUpgrade(i); },
  /* 直接授予某個項目 n 級。給測試與除錯主控台用 ——
     進化需要「滿級武裝 + 滿級被動」，實際玩到那裡太慢，驗證不了。 */
  grant: function (id, n) { for (var g = 0; g < (n || 1); g++) take(id); },
  loadout: function () { return { w: weaponsRef.slice(), p: passivesRef.slice() }; },
  state: STATE,
  /* 渲染統計。draw call 與模擬成本要分開量 —— 模擬慢要改演算法，
     渲染慢要減 draw call，兩者的手段完全不同（`tools/density-perf.js`）。 */
  /** 鎖住機庫預覽的角度（造型比對用）。傳 null 恢復閒置自轉。 */
  pose: function (rad) { setPose(rad); },
  /* 視覺診斷用的參考。**視覺缺陷不能靠眼睛從症狀反推**（lessons 2026-08-01）——
     要能分離變因：關掉某個群組、單獨調一盞燈、印出實際的材質色，各拍一張比對。
     不參與任何遊戲邏輯。 */
  /* `camOff` 可以直接改：驗證機體動畫時要把鏡頭拉近，
     戰場的俯視鏡頭下機體只有 60 像素，縮圖裡看不出接縫（記過一次）。 */
  /* `enemies` 是 M13 加的：Boss 造型驗收要把鏡頭擺到**那一隻**身上，
     而戰場鏡頭是綁玩家的 —— 沒有這個參考就只能靠猜座標。 */
  /* `codexFit` 是圖鑑的自動 fit 中間值。加進來的理由跟 `camera` 一樣：
     「畫面看起來太近」有三種可能（fov / 距離 / 版面補正），眼睛分不出來。 */
  /* `missiles`：投射物池。診斷「特效看不到」時要能分開「沒生成」與
     「沒被畫到」（lessons 2026-08-05），而那兩件事只有池子的狀態分得出來。
     `tools/titan-arts-shot.mjs` 讀它。 */
  dbg: { scene: scene, camera: camera, hangar: hangarGroup, P: P, enemies: enemies, hemi: hemi, sun: sun, rim: rim, rim2: rim2, camOff: CAM_OFF, codexFit: CODEX_FIT, missiles: missiles },
  render: function () {
    if (!renderer) return null;
    /* `field` 把敵人層單獨列出來（型別數 × 每型 part 數 + 2 個共用的）。
       只看整場的 `calls` 分不出「敵人層爆掉」與「場景本來就重」——
       規格 §4 的預算是訂在敵人層上的，要驗它就得能單獨量到它。 */
    var f = enemyField().stats();
    /* `field`/`kinds` 是**這一幀真的有畫的**敵人層成本（`count === 0` 的
       InstancedMesh 不產生 draw call，見 `enemy-field.ts` 的 `stats()`）。
       只看整場的 `calls` 分不出「敵人層爆掉」與「場上有一隻真 rig 的 Boss」
       —— 規格 §4 的預算是訂在敵人層上的，要驗它就得能單獨量到它。 */
    return { calls: renderer.info.render.calls, tris: renderer.info.render.triangles,
      enemies: enemies.length, field: f.live, kinds: f.alive, fieldReg: f.meshes };
  },
  /* `codex()` 是 B3 的取景門檻讀數：展品的包圍盒投影之後佔畫面高／寬幾成。
     「取景太遠」用眼睛看每一張都覺得「還好」，只有量得出來才修得掉 ——
     門檻是佔掃描艙高度 60% 以上（舊版約 25%）。 */
  codex: function () { return codexFrame(); },
  /* 截圖工具用的暫停。**不是遊戲功能** —— 它存在的理由是
     playwright 的 `page.screenshot()` 會等 `document.fonts.ready`，
     而同屏 300 隻在軟體渲染下會把主執行緒吃滿，那個 Promise 永遠不 resolve
     （症狀是「waiting for fonts to load」逾時，看起來像截圖壞了）。 */
  pause: function (v?: boolean) {
    STATE.paused = v === undefined ? !STATE.paused : !!v;
    return STATE.paused;
  },
  /* B2 的 Boss 讀數。**畫面上看不出來的東西要用讀數補**：階段序號、
     無敵／護盾窗口、剩餘弱點、實際畫了幾顆 mesh（骨段合併有沒有生效）。
     截圖驗收時跟畫面並排看 —— 只有畫面的話，「牠現在打不動」與
     「我打不到牠」分不出來。 */
  /* 飛行讀數。畫面上分不出「在頂點」與「剛開始掉」，也分不出
     「能量不夠」與「按鍵沒吃到」—— 這幾個數字就是那些差別。
     `ndc.y` 是玩家在畫面上的高度（+1 是上緣）：鏡頭只跟 60%，
     飛高時玩家會往上跑，跑出畫面就是這個數字先告訴你。 */
  flight: function () {
    return {
      y: +P.y.toFixed(2), vy: +P.vy.toFixed(1), flying: !!P.flying,
      thrust: Math.round(P.thrust), max: Math.round(P.s.thrustMax || 0),
      need: Math.round(takeoffCost()), airT: +P.airT.toFixed(1),
      airMul: +(P.airMul || 1).toFixed(2),
      player: P.rig ? objNdc(P.rig.root) : null
    };
  },
  boss: function () {
    if (!boss || !boss.alive) return null;
    var n = 0;
    boss.root.traverse(function (o: any) { if (o.isMesh) n++; });
    return {
      id: boss.type, stg: boss.stg, stages: boss.T.stages,
      hp: +(boss.hp / boss.hpMax).toFixed(3),
      inv: +(boss.invulnT || 0).toFixed(2), shield: boss.shield || 0, adds: boss.adds || 0,
      parts: boss.partHp ? boss.partHp.map(function (h) { return Math.round(h); }) : null,
      mesh: n, worldH: boss.T.worldH, enemies: enemies.length,
      /* **牠在畫面上的哪裡**（NDC，±1 是畫面邊緣）。
         「Boss 不在畫面裡」用眼睛看每一張都只能說「好像不見了」，
         量出來才知道是太遠、在鏡頭後面、還是被推到畫面上方。
         `dist` 是與玩家的水平距離，對照 `sim/boss-ai.ts` 的交戰帶。 */
      dist: +Math.hypot(boss.pos.x - P.pos.x, boss.pos.z - P.pos.z).toFixed(1),
      ndc: objNdc(boss.root),
      /* **玩家在畫面上剩多大**。Boss 的體型與鏡頭是一組取捨：
         退得夠遠才框得下大 Boss，而退遠的代價全部由玩家機體付
         （使用者 2026-08-02 明講不要「看著一個棋子」）。
         兩個數字要並排看才選得出來。 */
      player: P.rig ? objNdc(P.rig.root) : null
    };
  },
  /* 場地的驗收鉤子（`tools/map-shot.js`）。三個都只給工具用，不進玩法。

     ⚠️ **版面與質感都只能用眼睛驗**，但用眼睛驗要有紀律：俯視看版面、
     遊戲鏡頭看質感、讀數看密度。只看一張遊戲鏡頭截圖的話，
     「東西擠在一起」與「鏡頭剛好對著一堆東西」分不出來。 */
  mapBuild: function (site, idx) {
    var S = STAGES[Math.max(0, Math.min(STAGES.length - 1, idx | 0))];
    applyTheme(S); buildTerrain(S, idx | 0, site);
    return api.mapStats();
  },
  /** 視覺層的參考物件。**存在的理由是「畫面怪怪的」不能用眼睛反推**
      （`lessons.md` 2026-08-02）：要分離變因就得能在瀏覽器主控台把
      某一盞燈關掉、把某一層 `visible = false` 各拍一張。 */
  gfx: function () {
    return {
      scene: scene, camera: camera, sun: sun, hemi: hemi, rim: rim, rim2: rim2,
      floor: floorMesh, decal: macroMesh, cloud: cloudMesh, sky: skyMesh,
      dust: dustMesh, ash: ashMesh, far1: farSkyline, far2: farSkyline2,
      terrain: terrainGroup, fog: scene.fog,
      /* 接地影（`render/contact-shadow.ts`）。要能單獨關掉才驗得出它的貢獻 —— 見這個物件上面那段。 */
      contact: contactShadowMesh(),
    };
  },
  /** 自由視角：`fly()` 開關、`flyTo(x,y,z,yaw,pitch)` 直接定位（截圖工具用）。 */
  fly: function (on?) { return toggleFly(on); },
  flyTo: function (x, y, z, yaw, pitch) { return flyTo(x, y, z, yaw, pitch); },
  /** 後製開關（截圖對照與效能歸因用）。不帶參數＝只讀。 */
  postfx: function (on?) { if (on !== undefined) setPostFX(on); return postActive(); },
  /** 泛光調參（實看掃參數用）。`{strength, radius, threshold}`，只帶要改的欄位。 */
  postTune: function (o) { tunePostFX(o || {}); return BLOOM; },
  /** 畫質（截圖對照與效能歸因用）。不帶參數＝只讀。 */
  quality: function (lv?) { return lv ? setQuality(lv) : QUALITY; },
  /* 武器特效的活口數。**視覺驗收要有數字**：截圖上「沒看到爆炸」有兩種可能
     ——特效沒生出來，或生出來但太小太暗。這個讀數把兩者分開，
     不用靠眼睛猜（2026-08-01 記過：機制沒接上會被誤讀成別的問題）。 */
  vfx: function (on?) {
    if (on !== undefined) vfxEnable(!!on);
    return { ready: vfxReady(), alive: vfxAlive(), probe: vfxProbe() };
  },
  /** `h > 0` 切成正上方的俯視、`0` 還原成遊戲鏡頭。 */
  mapTop: function (h) {
    mapTopH = h || 0;
    return mapTopH;
  },
  mapStats: function () {
    var stand = 0, low = 0, cover = 0;
    for (var i = 0; i < obstacles.length; i++) {
      var o: any = obstacles[i];
      if (o.stand) stand++;
      if (o.h <= VAULT) low++;
      cover += o.kind === 1 ? Math.PI * o.r * o.r : o.hw * o.hd * 4;
    }
    return {
      obstacles: obstacles.length, stand: stand, lowCover: low, ramps: ramps.length,
      coverPct: +(cover / arenaArea() * 100).toFixed(1),
      /* 接地影的圈數（`render/contact-shadow.ts`）。**驗收判準是「不是 0」** ——
         足跡收集接在 `put()` 上，任何一次建造流程改動都可能靜靜斷掉，
         而症狀（東西浮在地上）在俯視縮圖裡看不出來。 */
      contactShadows: contactShadowMesh() ? contactShadowMesh()!.count : 0,
      calls: renderer ? renderer.info.render.calls : 0,
      tris: renderer ? renderer.info.render.triangles : 0
    };
  },
  startRun: function (mech, seed) { startRun(mech, seed); },
  steps: function () { return simSteps; },
  snapshot: function () {
    var es = [];
    for (var i = 0; i < enemies.length; i++) es.push([enemies[i].type, enemies[i].pos.x, enemies[i].pos.z, enemies[i].hp,
      enemies[i].impact, enemies[i].staggerT, enemies[i].staggerCd]);
    return {
      steps: simSteps, runT: STATE.runT, stage: STATE.stage, phase: STATE.phase, won: STATE.won,
      px: P.pos.x, pz: P.pos.z, hp: P.hp, sh: P.sh, scrap: P.scrap,
      /* 飛行是模擬狀態，一定要進快照 —— 漏掉的話「同種子兩次跑出不同高度」
         這種岔開不會被決定性測試抓到（hud.ts:157 那條規則記過三次的坑）。 */
      py: P.y, vy: P.vy, thrust: P.thrust,
      /* `bossUp` 是**還沒領的 Boss 獎勵**，跟 `pendingLv` 同性質的佇列 ——
         不進快照的話「同種子兩場欠的獎勵次數不同」是隱形的。
         ⚠️ 它讓雜湊換一個值，但那是欄位進 JSON 造成的，與行為無關。 */
      lv: P.lv, xp: P.xp, pendingLv: P.pendingLv, bossUp: P.bossUp, build: loadoutDigest(),
      faceMove: P.faceMove, faceAim: P.faceAim, lock: enemies.indexOf(P.lockT), lockOn: P.lockOn,
      rng: RNG.s, enemies: es
    };
  }
};

/** 一個物件的包圍盒在畫面上的位置與佔比（NDC）。回 null = 有角落在鏡頭後面。 */
function objNdc(root) {
  var box = new THREE.Box3().setFromObject(root);
  if (box.isEmpty() || !isFinite(box.min.y) || !isFinite(box.max.y)) return null;
  var lo = 1e9, hi = -1e9, le = 1e9, ri = -1e9, v = new THREE.Vector3();
  for (var i = 0; i < 8; i++) {
    v.set(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z);
    v.applyMatrix4(camera.matrixWorldInverse);
    if (v.z > -0.05) return null;                 // 在鏡頭後面
    v.applyMatrix4(camera.projectionMatrix);
    lo = Math.min(lo, v.y); hi = Math.max(hi, v.y);
    le = Math.min(le, v.x); ri = Math.max(ri, v.x);
  }
  return { x: +((le + ri) / 2).toFixed(2), y: +((lo + hi) / 2).toFixed(2),
    w: +(ri - le).toFixed(2), h: +(hi - lo).toFixed(2),
    /* 完全在框內才算「看得到」。±1 是畫面邊緣。 */
    inFrame: le > -1 && ri < 1 && lo > -1 && hi < 1 };
}

if (typeof window !== 'undefined') window.__CHASSIS_DEBUG = api;
